import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Editor, EditorTextChangeEvent } from "primereact/editor";
import { useAppTheme } from "../../context/ThemeContext";
import {
  EmailTemplate,
  useGetEmailTemplatesQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useSeedDefaultTemplatesMutation,
} from "../../services/emailTemplates.service";
import ToastCustom from "../../hooks/ToastCustom";

const VARIABLES = [
  "{{fullName}}",
  "{{email}}",
  "{{refNo}}",
  "{{university}}",
  "{{program}}",
  "{{phone}}",
  "{{status}}",
];

const EMPTY: Omit<EmailTemplate, "_id" | "createdAt" | "updatedAt"> = {
  name: "",
  subject: "",
  html: "",
  category: "",
};

function TemplateEditor({
  open,
  initial,
  onClose,
  onSave,
  saving,
}: {
  open: boolean;
  initial: typeof EMPTY & { _id?: string };
  onClose: () => void;
  onSave: (data: typeof EMPTY) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);

  React.useEffect(() => {
    setForm(initial);
  }, [initial, open]);

  const set = (field: keyof typeof EMPTY, val: string) =>
    setForm((p) => ({ ...p, [field]: val }));

  const valid = form.name.trim() && form.subject.trim() && form.html.trim();

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {initial._id ? "Edit Template" : "New Template"}
        <IconButton sx={{ ml: "auto" }} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField
              label="Template Name"
              fullWidth
              size="small"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Category (optional)"
              fullWidth
              size="small"
              value={form.category ?? ""}
              onChange={(e) => set("category", e.target.value)}
              placeholder="e.g. Onboarding"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Subject"
              fullWidth
              size="small"
              value={form.subject}
              onChange={(e) => set("subject", e.target.value)}
              required
              helperText="You can use variables like {{fullName}} in the subject"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
              Body
            </Typography>
            <Paper variant="outlined">
              <Editor
                style={{ minHeight: 200 }}
                value={form.html}
                onTextChange={(e: EditorTextChangeEvent) =>
                  set("html", e.htmlValue ?? "")
                }
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
              Available variables — click to copy
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {VARIABLES.map((v) => (
                <Chip
                  key={v}
                  label={v}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(v);
                    ToastCustom.success(`${v} copied`);
                  }}
                  sx={{ fontFamily: "monospace", fontSize: "0.72rem", cursor: "pointer" }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          endIcon={saving ? <CircularProgress size={14} /> : undefined}
          onClick={() => onSave(form)}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EmailTemplatesPage() {
  const { isNewTheme, accentColor } = useAppTheme();
  const { data: templates = [], isLoading } = useGetEmailTemplatesQuery();
  const [createTemplate, { isLoading: creating }] = useCreateEmailTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateEmailTemplateMutation();
  const [deleteTemplate] = useDeleteEmailTemplateMutation();
  const [seedDefaults, { isLoading: seeding }] = useSeedDefaultTemplatesMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<(typeof EMPTY & { _id?: string }) | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<EmailTemplate | null>(null);

  const openNew = () => {
    setEditing({ ...EMPTY });
    setEditorOpen(true);
  };

  const openEdit = (t: EmailTemplate) => {
    setEditing({ _id: t._id, name: t.name, subject: t.subject, html: t.html, category: t.category ?? "" });
    setEditorOpen(true);
  };

  const handleSave = async (form: typeof EMPTY) => {
    try {
      if (editing?._id) {
        await updateTemplate({ id: editing._id, body: form }).unwrap();
        ToastCustom.success("Template updated");
      } else {
        await createTemplate(form).unwrap();
        ToastCustom.success("Template created");
      }
      setEditorOpen(false);
    } catch {}
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteTemplate(deleteConfirm._id).unwrap();
      ToastCustom.success("Template deleted");
    } catch {}
    setDeleteConfirm(null);
  };

  const grouped = React.useMemo(() => {
    const map: Record<string, EmailTemplate[]> = {};
    templates.forEach((t) => {
      const key = t.category?.trim() || "General";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [templates]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={isNewTheme ? 700 : 600}>
            Email Templates
          </Typography>
          {isNewTheme && (
            <Box sx={{ width: 36, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />
          )}
        </Box>
        <Button
          variant="outlined"
          disabled={seeding}
          endIcon={seeding ? <CircularProgress size={14} /> : undefined}
          onClick={async () => {
            try {
              const res = await seedDefaults().unwrap();
              ToastCustom.success(
                `Loaded ${res.created} template${res.created !== 1 ? "s" : ""}${res.skipped ? ` (${res.skipped} already existed)` : ""}`
              );
            } catch {}
          }}
        >
          {seeding ? "Loading…" : "Load Defaults"}
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          New Template
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : templates.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
          <Typography color="text.secondary" gutterBottom>No templates yet.</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Load the built-in bilingual templates (English + Arabic) or create your own.
          </Typography>
          <Button
            variant="contained"
            disabled={seeding}
            endIcon={seeding ? <CircularProgress size={14} /> : undefined}
            onClick={async () => {
              try {
                const res = await seedDefaults().unwrap();
                ToastCustom.success(`Loaded ${res.created} default templates`);
              } catch {}
            }}
            sx={{ mr: 1.5 }}
          >
            {seeding ? "Loading…" : "Load Default Templates"}
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openNew}>
            Create manually
          </Button>
        </Paper>
      ) : (
        Object.entries(grouped).map(([category, items]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: isNewTheme ? "#94A3B8" : "text.disabled",
                mb: 1,
                display: "block",
              }}>
              {category}
            </Typography>
            <Grid container spacing={2}>
              {items.map((t) => (
                <Grid item xs={12} sm={6} md={4} key={t._id}>
                  <Card
                    sx={
                      isNewTheme
                        ? { borderRadius: 3, border: "1px solid #E2E8F0", boxShadow: "none" }
                        : {}
                    }>
                    <CardContent sx={{ pb: "8px !important" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={600} noWrap>
                            {t.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ mt: 0.25 }}>
                            {t.subject}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", ml: 1, flexShrink: 0 }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(t)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteConfirm(t)}
                              sx={{ color: "error.main" }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mt: 1, display: "block" }}>
                        {new Date(t.updatedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {editing && (
        <TemplateEditor
          open={editorOpen}
          initial={editing}
          onClose={() => setEditorOpen(false)}
          onSave={handleSave}
          saving={creating || updating}
        />
      )}

      <Dialog open={!!deleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
